import { IconCheckCircleFilled } from "@tine/designsystem-icons/sharp";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import { logger } from "../../common/logger";
import type { ContentfulSignupButton } from "../../common/types/cmsTypes";
import type { DynamicsCaptureConfig } from "../../common/types/dynamicsTypes";
import type { FormValuesToSubmit } from "../../common/types/formTypes";
import type { SignUpRepeaterData } from "../../common/types/promotionTypes";
import { dynamicsEndpoint } from "../../common/utils/dynamicsUtils";
import { getModuleVisibility } from "../../common/utils/segmentUtils";
import authStore from "../../stores/auth/authStore";
import contentStore from "../../stores/cms/contentStore";
import segmentStore from "../../stores/cms/segmentStore";
import promotionStore from "../../stores/promotionStore";
import { AdventCalendarDoorContext } from "./advent-calendar/AdventCalendarDoorContext";
import { getAdventCalendarSignupButtonCode } from "./advent-calendar/adventCalendarUtils";
import CmsPromotionSignupForm from "./promotion/CMSPromotionSignupForm";

type CmsPromotionSignupProps = {
   content: ContentfulSignupButton;
};

const CmsPromotionSignup = ({ content }: CmsPromotionSignupProps) => {
   const calendarDoor = useContext(AdventCalendarDoorContext);
   const promotionId = getAdventCalendarSignupButtonCode(content.fields.promotionId, calendarDoor);
   if (!promotionId) {
      return null;
   }
   const company = authStore.getCurrentCompany();
   const user = authStore.user;

   const isVisible = getModuleVisibility(content, segmentStore.currentCustomerProperties);
   if (!isVisible) {
      return null;
   }

   const loggedIn = contentStore.isPreviewMode ? true : authStore.isLoggedIn();
   if (!loggedIn) {
      return content.fields.notLoggedInText ? <p>{content.fields.notLoggedInText}</p> : null;
   }

   const currentlySignedup = promotionStore.isSignedUpTo(promotionId);
   const dynamicsEventId = content.fields.dynamicsEventId || "";
   const voucherifyEventName = content.fields.voucherifyEventName || "";

   const fields = Array.isArray(content.fields.additionalFields)
      ? (content.fields.additionalFields as SignUpRepeaterData)
      : ([] as SignUpRepeaterData);

   const mapping = Array.isArray(content.fields.dynamicsMapping)
      ? (content.fields.dynamicsMapping as SignUpRepeaterData)
      : ([] as SignUpRepeaterData);

   const dynamicsConfig: DynamicsCaptureConfig = {
      FormApiUrl: dynamicsEndpoint,
      FormId: dynamicsEventId
   };

   const columnStyle = "w-full mx-auto max-w-md";

   const signup = async (values: FormValuesToSubmit): Promise<boolean> => {
      try {
         const signedUp = await promotionStore.signUpToPromotion(promotionId, voucherifyEventName, dynamicsEventId, values);
         if (!signedUp) {
            return false;
         }
         await promotionStore.fetchAvailablePromotions();
         await promotionStore.fetchPromotionDiscountsWithoutDelay();
         return true;
      } catch (err) {
         logger.warn("contentful", "Error signing up to promotion", err);
         return false;
      }
   };
   if (!currentlySignedup && company && user) {
      return (
         <div className={cn(columnStyle, "px-2 text-center")}>
            <CmsPromotionSignupForm
               additionalFields={fields}
               dynamicsMapping={mapping}
               dynamicsConfig={dynamicsConfig}
               buttonText={content.fields.buttonText}
               signup={signup}
               company={company}
               user={user}
            />
         </div>
      );
   }
   return (
      <div className={columnStyle}>
         <p className="px-2 py-6 text-center">
            <IconCheckCircleFilled className="inline-block" fill="green" /> {content.fields.alreadySignedUpFeedback}
         </p>
      </div>
   );
};

export default observer(CmsPromotionSignup);
